import { Svg, theme as coreUiTheme } from '@hopskipdrive/react-components';
import { createTheme, CssBaseline, StyledEngineProvider, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { createGlobalStyle } from 'styled-components';

import { colorTokens, font, variables } from './variables';

const theme = createTheme({
  ...coreUiTheme,
  spacing: 4,
  palette: {
    primary: {
      ...colorTokens.primary,
    },
    secondary: {
      ...colorTokens.secondary,
    },
    error: {
      ...colorTokens.error,
    },
    warning: {
      ...colorTokens.warning,
    },
    info: {
      ...colorTokens.info,
    },
    success: {
      ...colorTokens.success,
    },
  },
  typography: {
    fontFamily: "'CentraNo2', Arial, 'sans-serif'",
    fontSize: 16,
    h1: {
      fontSize: '96px',
      fontWeight: 500,
      margin: 0,
      lineHeight: '116.7%',
      letterSpacing: '-1.5px',
    },
    h2: {
      fontSize: '60px',
      fontWeight: 500,
      lineHeight: '120%',
      letterSpacing: '-0.5px',
    },
    h3: {
      fontSize: '48px',
      fontWeight: 500,
      lineHeight: '116.7%',
    },
    h4: {
      fontSize: '34px',
      fontWeight: 500,
      lineHeight: '123.5%',
      letterSpacing: '0.25px',
    },
    h5: {
      fontSize: '24px',
      fontWeight: 500,
      lineHeight: '133.4%',
    },
    h6: {
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '160%',
      letterSpacing: '0.15px',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '175%',
      letterSpacing: '0.15px',
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '157%',
      letterSpacing: '0.1px',
    },
    body1: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '150%',
      letterSpacing: '0.15px',
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },
    button: {
      fontSize: '16px',
      fontWeight: 500,
      textTransform: 'capitalize',
      color: colorTokens.text.primary,
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },
    overline: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '266%',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiCssBaseline: {
      ...coreUiTheme.components?.MuiCssBaseline,
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 300,
          fontSize: `${font.size.base}px`,
          lineHeight: '17px',
          margin: '5px 16px 0',
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: colorTokens.info.main,
          fontWeight: 500,
          lineHeight: '175%',
          letterSpacing: '0.15px',
          '&:hover': {
            color: colorTokens.info.main,
          },
          '&:active': {
            textDecoration: 'none',
            color: colorTokens.info.dark,
          },
        },
        underlineNone: {
          '&:hover': {
            color: colorTokens.info.light,
          },
          '&:active': {
            textDecoration: 'none',
            color: colorTokens.info.dark,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: '40px',
          background: colorTokens.background.default,
          border: '1px solid #CBD1D6',
          boxSizing: 'border-box',
          borderRadius: '12px',
          fontSize: '13px',
          color: '#222222',
          lineHeight: 1.4,
          '&.Mui-focused': {
            border: `2px solid ${colorTokens.primary.main}`,
          },
          '&.Mui-disabled': {
            background: colorTokens.background.disabled,
            border: '1px solid #CBD1D6',
            pointerEvents: 'none',
          },
          '&.Mui-error': {
            borderColor: colorTokens.error.main,
            background: colorTokens.background.default,
          },
        },
        input: {
          paddingTop: 0,
          paddingBottom: 0,
        },
        multiline: {
          padding: '10px 16px',
          borderRadius: '20px',
        },
        notchedOutline: {
          display: 'none',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: '8px',
          paddingBottom: '8px',
          fontWeight: 300,
          '&.selected': {
            background: 'none',
            color: colorTokens.info.main,
            fontWeight: 500,
            '&:hover': {
              background: colorTokens.info.light,
            },
          },
          '&:hover': {
            background: colorTokens.info.light,
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          fontWeight: 500,
          fontSize: 13,
          padding: '12px 24px',
          lineHeight: '19px',
          backgroundColor: colorTokens.primary.contrast,
          opacity: 0.85,
          maxWidth: '195px',
          color: colorTokens.text.primary,
          borderRadius: '5px',
          border: `1px solid ${colorTokens.border.main}`,
          boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.07)',
        },
        tooltipPlacementBottom: {
          margin: '8px 0',
        },
        tooltipPlacementTop: {
          margin: '8px 0',
        },
        arrow: {
          '&:before': {
            border: `1px solid ${colorTokens.border.main}`,
          },
          color: colorTokens.primary.contrast,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation8: {
          marginTop: '8px',
          boxShadow: '0px 4px 15px rgba(29, 75, 122, 0.11)',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(168, 176, 184, 0.95)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {},
        paper: {
          boxShadow: '0px 0px 14px rgba(77, 88, 147, 0.1)',
          margin: 0,
          minWidth: 340,
        },
        paperWidthSm: {
          maxWidth: 360,
        },
        paperWidthMd: {
          maxWidth: 560,
        },
        paperWidthLg: {
          maxWidth: 780,
        },
        paperWidthXl: {
          maxWidth: 990,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 30px 16px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 31px',
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: '150%',
          letterSpacing: '0.15px',
          color: colorTokens.text.primary,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '20px 30px',
          justifyContent: 'space-between',
        },
      },
    },
    MuiTouchRipple: {
      styleOverrides: {
        root: {
          display: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '84px',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '16px',
          lineHeight: '150%',
          height: '32px',
          minWidth: '90px',
          whiteSpace: 'nowrap',
          gap: '6px',
        },
        sizeLarge: {
          height: '38px',
        },
        sizeMedium: {
          height: '32px',
        },
        sizeSmall: {
          height: '30px',
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        slots: {
          closeIcon: () => <Svg icon="cross" />,
        },
      },
      styleOverrides: {
        standardError: {
          color: colorTokens.error.dark,
        },
        standardWarning: {
          color: colorTokens.warning.dark,
        },
        standardInfo: {
          color: colorTokens.info.dark,
        },
        standardSuccess: {
          color: colorTokens.success.dark,
        },
        outlinedError: {
          backgroundColor: 'rgba(255, 216, 210, 0.60)',
          color: colorTokens.error.dark,
        },
        outlinedWarning: {
          color: colorTokens.warning.dark,
        },
        outlinedInfo: {
          color: colorTokens.info.dark,
        },
        outlinedSuccess: {
          color: colorTokens.success.dark,
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontSize: '20px',
          fontWeight: 500,
          lineHeight: '27px',
          letterSpacing: '0.15px',
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        clearIcon: <Svg icon="cancel-filled" />,
      },
      styleOverrides: {
        paper: {
          fontWeight: 200,
          borderRadius: '20px',
          padding: '6px 0px',
          boxShadow: '0px 4px 15px rgba(77, 88, 147, 0.2)',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '175%',
          letterSpacing: '0.15px',
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          display: 'inline-flex',
          padding: '6px 8px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '2px',
          border: `1px solid ${colorTokens.primary.outlineBorder}`,
          color: colorTokens.primary.main,
          backgroundColor: colorTokens.chip.default,
          borderColor: colorTokens.primary.outlineBorder,
        },
        sizeMedium: {
          minHeight: '27px',
          padding: '2px 4px',
        },
        deleteIcon: {
          margin: 0,
          color: colorTokens.primary.outlineBorder,
        },
        icon: {
          margin: 0,
          color: colorTokens.primary.outlineBorder,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'stretch',
          minWidth: '143.6px',
          fontSize: '16px',
          textTransform: 'none',
          padding: '13px 12px',
          overflow: 'unset',
          color: colorTokens.primary.main,
          opacity: 1,
          height: '56px',
          transition: 'background-color 0.3s ease-out, color 0.3s ease-out',
          svg: {
            width: '24px',
            height: '24px',
          },
          '&.Mui-disabled': {
            color: colorTokens.primary.disabled,
            opacity: 0.5,
          },
          '&.Mui-selected': {
            color: colorTokens.primary.contrast,
            backgroundColor: colorTokens.primary.main,
            '&:hover': {
              transition: 'background-color 0.3s ease-out, color 0.3s ease-out',
              backgroundColor: colorTokens.primary.dark,
            },
          },
          '&:hover': {
            transition: 'background-color 0.3s ease-out, color 0.3s ease-out',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          button: {
            minHeight: '18px',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        sizeSmall: {
          height: '24px',
          width: '24px',
          '& svg': {
            width: '16px',
            height: '16px',
          },
        },
        root: {
          border: `1px solid ${colorTokens.primary.borderDark}`,
          opacity: 0.9,

          '&.Mui-focused': {
            border: `2px solid ${colorTokens.secondary.main}`,
          },
          '&:hover': {
            background: colorTokens.tonal.contrast,
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          height: '100%',
        },
      },
    },
    ...variables,
  },
});

const GlobalStyle = createGlobalStyle`
  body {
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-family: 'CentraNo2', Arial, Helvetica, sans-serif;
  }
  body, html {
    height: 100%;
  }
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
`;

const ThemeProvider = ({ children }): JSX.Element => (
  <StyledEngineProvider injectFirst>
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyle />
      {children}
    </MuiThemeProvider>
  </StyledEngineProvider>
);

export { theme, ThemeProvider };
